import type { CalloutType } from '@/components/molecules/docs/Callout';

export interface GuideCallout {
  type: CalloutType;
  /** Overrides the default type label (e.g. "Psst" instead of "Tip"). */
  label?: string;
  text: string;
}

export interface GuideStep {
  title: string;
  body?: string;
  code?: string;
}

export interface GuideImage {
  /** Path under /public. */
  src: string;
  alt: string;
  /** width/height, used to reserve layout space before it loads. */
  aspect?: number;
}

export interface GuideSubsection {
  title: string;
  lede?: string;
  checklist?: string[];
  image?: GuideImage;
  calloutsAfter?: GuideCallout[];
}

export interface GuideProcedure {
  title: string;
  /** Intro paragraph shown after the meta row and before any steps. */
  lede?: string;
  timeRequired?: string;
  difficulty?: string;
  prerequisites?: string;
  calloutsBefore?: GuideCallout[];
  steps?: GuideStep[];
  image?: GuideImage;
  subsections?: GuideSubsection[];
  calloutsAfter?: GuideCallout[];
}

export interface GuideCategory {
  number: number;
  title: string;
  overviewLede?: string;
  overviewImage?: GuideImage;
  overviewCallouts?: GuideCallout[];
  procedures: GuideProcedure[];
}

export interface UserGuideData {
  title: string;
  subtitle: string;
  intro: string;
  popularLinks: { label: string; procedureId: string }[];
  homeCallouts: GuideCallout[];
  categories: GuideCategory[];
}

export const userGuide: UserGuideData = {
  title: 'User Guide',
  subtitle: 'Self-service guide',
  intro:
    "This guide walks you through using nCoder.ai to connect, explore, and modernize your Salesforce org yourself — from your first login to production deployment. Each numbered category groups its own step-by-step procedures, in the order you'll typically use them.",
  popularLinks: [
    { label: 'Connecting a Salesforce Org', procedureId: 'connecting-a-salesforce-org' },
    { label: 'Creating a Schedule', procedureId: 'creating-a-schedule' },
  ],
  homeCallouts: [
    {
      type: 'note',
      text: 'Some steps below may not apply to your specific stack, hosting environment, or licensing tier. Confirm each procedure against your own environment before running it.',
    },
    {
      type: 'caution',
      text: 'Always run modernization and migration procedures against a staging environment or a branch first, never directly against production. Take a full backup/snapshot before any data migration step.',
    },
    {
      type: 'tip',
      text: 'Each procedure lists estimated time, prerequisites, and a rollback note where relevant — read the whole procedure once before starting.',
    },
  ],
  categories: [
    {
      number: 1,
      title: 'Getting Started',
      overviewLede: 'Install nCoder.ai and set up your workspace before connecting any systems.',
      procedures: [
        {
          title: 'How to Access nCoder',
          timeRequired: '2 minutes',
          difficulty: 'Beginner',
          prerequisites: 'Browser',
          lede: 'nCoder.ai is accessed at [https://my.ncoder.ai/auth/login](https://my.ncoder.ai/auth/login). From there you can connect nCoder.ai directly to your Salesforce org.',
          calloutsAfter: [
            { type: 'note', text: 'nCoder is a web application and we support any OS.' },
            {
              type: 'caution',
              label: 'Special Note',
              text: "Your credentials are never stored. nCoder.ai uses Salesforce OAuth 2.0, so authentication happens directly with Salesforce and nCoder.ai never sees or retains your username or password.",
            },
          ],
        },
      ],
    },
    {
      number: 2,
      title: 'Connecting to Your Salesforce Org',
      overviewLede: 'Before nCoder.ai can assess or modernize anything, it needs to connect to your Salesforce org and read your metadata.',
      overviewCallouts: [
        {
          type: 'tip',
          label: 'Psst',
          text: "Relax — we're nosy about your metadata, not your data. We're here for your Apex classes and object schemas, not your customer records.",
        },
        { type: 'caution', text: 'Use a read-only or scoped service account for the initial connection. Do not use personal admin credentials.' },
      ],
      procedures: [
        {
          title: 'Connecting a Salesforce Org',
          timeRequired: '10 minutes',
          difficulty: 'Beginner',
          prerequisites: "Salesforce admin access, a Connected App (or permission to create one)",
          calloutsBefore: [
            {
              type: 'note',
              text: 'You can also sign in and connect your Salesforce org directly from the web console at [https://my.ncoder.ai/auth/login](https://my.ncoder.ai/auth/login) — see Getting Started → How to Access nCoder for a walkthrough of that screen.',
            },
          ],
          steps: [
            { title: 'Open Connection Manager', body: 'In the left-hand sidebar, click **Connection Manager**.' },
            { title: 'Click Add Connection', body: 'On the Connection Manager page, click **+ Add Connection** in the top right.' },
            {
              title: 'Select your environment',
              body: 'Choose **Production**, **Developer Org**, or **Sandbox**. Note that Sandboxes cannot host a Dev Hub — pick Production or Developer Org if you need scratch org creation.',
            },
            { title: 'Click Connect', body: "You'll be redirected to Salesforce to sign in and authorize nCoder.ai via OAuth 2.0." },
            {
              title: 'Approve the requested scopes',
              body: 'Review the permissions Salesforce lists and approve them (read-only recommended for the initial connection).',
            },
          ],
          image: {
            src: '/release-notes/connection-manager.jpg',
            alt: 'nCoder.ai Connection Manager showing connected Salesforce organizations with a live metadata breakdown',
            aspect: 1.8717,
          },
          calloutsAfter: [
            {
              type: 'tip',
              text: 'Once connected, the org appears on the Connection Manager page along with a live metadata breakdown — objects, Apex classes, triggers, LWCs, flows, and profiles.',
            },
          ],
        },
        {
          title: "Connecting Multiple Salesforce Org's",
          timeRequired: '~5 minutes per org',
          difficulty: 'Beginner',
          prerequisites: 'At least one Salesforce org already connected (see Connecting a Salesforce Org)',
          lede: "You can connect any number of Salesforce orgs to nCoder.ai — there's no cap. This is handy if you're managing multiple business units, a multi-org architecture, or separate Production/Sandbox/Developer Org environments side by side.",
          steps: [
            {
              title: 'Go back to Connection Manager',
              body: 'Click **Connection Manager** in the left-hand sidebar — every org you\'ve already connected is listed here.',
            },
            { title: 'Click Add Connection again', body: 'Click **+ Add Connection** in the top right, just like you did for your first org.' },
            {
              title: 'Select the environment and connect',
              body: 'Choose **Production**, **Developer Org**, or **Sandbox** for the new org and click **Connect**.',
            },
            {
              title: 'Repeat as needed',
              body: 'Add as many orgs as you need — Production, Sandbox, and Developer Org connections can all coexist side by side.',
            },
          ],
          calloutsAfter: [
            {
              type: 'caution',
              label: 'Your Org, Your Rules',
              text: 'Your org is our context. Whatever you do — scan, refactor, migrate — happens inside that org, and only that org. No bleed-over, no cross-org surprises.',
            },
            {
              type: 'tip',
              text: 'Connection Manager also shows a live metadata breakdown across all connected orgs — objects, Apex classes, triggers, LWCs, flows, and profiles — so you can compare orgs at a glance.',
            },
          ],
        },
      ],
    },
    {
      number: 3,
      title: 'Org X-Ray',
      overviewLede:
        'Once your org is connected, use these tools to see inside it — browse metadata, generate documentation, ask questions in Natural Language, and scan for security issues, all without touching a line of code yet.',
      procedures: [
        {
          title: 'Metadata Explorer',
          timeRequired: '5 minutes',
          difficulty: 'Beginner',
          prerequisites: 'Connected Salesforce org (Section 2)',
          calloutsBefore: [
            {
              type: 'note',
              text: 'As soon as you connect your org, Metadata Explorer automatically kicks off a full metadata sync — no setup required. You can watch its progress from the Activity panel in the top right.',
            },
          ],
          steps: [
            { title: 'Open Metadata Explorer', body: 'Click **Metadata Explorer** in the left-hand sidebar.' },
            { title: 'Pick an org', body: 'If you have multiple orgs connected, choose which one to browse from the org switcher.' },
            {
              title: 'Drill into a metadata type',
              body: 'Browse objects, Apex classes, triggers, flows, LWCs, and profiles — click any item to see its details and relationships.',
            },
            { title: 'Search', body: 'Use the search bar to jump straight to a specific object or component by name.' },
          ],
          image: {
            src: '/release-notes/metadata-explorer.jpg',
            alt: "nCoder.ai Metadata Explorer showing an org's Salesforce metadata browsable by type",
            aspect: 1.7032,
          },
          calloutsAfter: [
            { type: 'tip', text: "Metadata Explorer is read-only and safe to click around in — nothing you do here changes your org." },
          ],
        },
        {
          title: 'Documentation',
          timeRequired: '2 minutes to browse',
          difficulty: 'Beginner',
          prerequisites: 'Connected Salesforce org (Section 2)',
          calloutsBefore: [
            {
              type: 'note',
              text: 'There\'s no "Generate" button to click — documentation is created automatically for every component as soon as your org\'s metadata syncs. Nothing to configure, nothing to scope.',
            },
          ],
          steps: [
            { title: 'Open Documentation', body: 'Click **Documentation** in the left-hand sidebar.' },
            { title: 'Browse or search', body: 'Pick a metadata type from the left panel, then select a component to view its documentation.' },
            { title: 'Export or share', body: 'Download the documentation or share a link with your team.' },
          ],
          image: {
            src: '/release-notes/documentation.jpg',
            alt: 'nCoder.ai Documentation page listing metadata types and generated component write-ups',
            aspect: 1.699,
          },
          subsections: [
            {
              title: "What's in a class's documentation",
              lede: "Click into any component (an Apex class, in this example) and nCoder.ai generates a full write-up — basically everything short of the class's astrological sign:",
              checklist: [
                'What It Does & When It Runs',
                'Security & Governor Notes',
                'What Breaks if Changed (Blast Radius)',
                'Recommendations',
                'Security & Sharing',
                'Hardcoded References',
                'Method References',
                'Class Diagram',
                'SOQL Queries',
                'External Callouts',
                'Internal Call Graph',
                'Impact Analysis',
                'Data Access Map',
              ],
            },
          ],
          calloutsAfter: [{ type: 'tip', text: 'Documentation is always generated and up to date — no user intervention required.' }],
        },
        {
          title: 'Text to SOQL',
          timeRequired: '~2 minutes per query',
          difficulty: 'Beginner',
          prerequisites: 'Connected Salesforce org (Section 2)',
          lede: 'Ask for the data you want in Natural Language — nCoder.ai translates it into SOQL for you.',
          steps: [
            { title: 'Open Text to SOQL', body: 'Click **Text to SOQL** in the left-hand sidebar.' },
            { title: 'Type your question', body: 'For example: "Show me all Opportunities closed this quarter over $50,000."' },
            { title: 'Review the generated query', body: 'Check the SOQL nCoder.ai produces before running it.' },
            { title: 'Run it', body: 'Execute the query and view results directly in the console.' },
          ],
          image: {
            src: '/release-notes/text-to-soql.jpg',
            alt: "nCoder.ai Text to SOQL screen translating a natural-language question into a SOQL query",
            aspect: 1.697,
          },
          calloutsAfter: [
            { type: 'note', text: "Text to SOQL runs read-only queries against your org's data — it won't create, update, or delete records." },
          ],
        },
        {
          title: 'Security Scanner',
          timeRequired: '5 minutes to review',
          difficulty: 'Beginner',
          prerequisites: 'Connected Salesforce org (Section 2)',
          calloutsBefore: [
            {
              type: 'note',
              text: "Your org is scanned for vulnerabilities automatically as soon as its metadata syncs — no setup required. You can also run a scan on demand at any time.",
            },
          ],
          steps: [
            { title: 'Open Security Scanner', body: 'Click **Security Scanner** in the left-hand sidebar.' },
            {
              title: 'Review the findings',
              body: 'Results are already there, broken down by severity — Critical, High, Medium, Low, and Info — plus an overall security score.',
            },
            { title: 'Run a fresh scan anytime (optional)', body: 'Click **Run fresh scan** in the top right if you want an on-demand re-check, for example right after a change.' },
            {
              title: 'Drill into a finding',
              body: "Search or filter by severity/category, then expand a file to see its issues. Use **Let's Weave** next to a finding to jump straight into fixing it.",
            },
            { title: 'Export for review', body: 'Click **Export Report** to share findings for a compliance review.' },
          ],
          image: {
            src: '/release-notes/security-scanner.jpg',
            alt: 'nCoder.ai Security Scanner showing findings by severity and an overall security score',
            aspect: 1.8642,
          },
          calloutsAfter: [
            {
              type: 'caution',
              text: "A security scan reads configuration and metadata only — it doesn't change any settings. Any fix it suggests still needs to be applied and reviewed by you.",
            },
          ],
        },
      ],
    },
    {
      number: 4,
      title: 'Agents',
      overviewLede:
        "nCoder.ai ships with a library of prebuilt agents, built from our team's extensive Salesforce implementation experience — tasks that help every developer, on every org. And the buck doesn't stop there: you can also create any number of your own agents. Sky's the limit.",
      overviewImage: {
        src: '/release-notes/agents.jpg',
        alt: 'nCoder.ai Agents page listing prebuilt agents for Apex quality, refactoring, testing, and more',
        aspect: 1.8568,
      },
      procedures: [
        {
          title: 'Using a Prebuilt Agent',
          timeRequired: '5 minutes',
          difficulty: 'Beginner',
          prerequisites: 'Connected Salesforce org (Section 2)',
          steps: [
            { title: 'Open Agents', body: 'Click **Agents** in the left-hand sidebar, under the Agents section.' },
            {
              title: 'Browse the library',
              body: "You'll find a set of ready-made agents covering common cleanup and modernization tasks — things like Apex Quality Fix, Apex Refactor, Apex Test Generator, Flow Migration, Object Cleanup, Org-Wide Sharing (OWD) Assistant, Permission Set Fix, Pricebook Builder, and Profile Standardization.",
            },
            { title: 'Search or filter', body: 'Use the search bar to find an agent by name, goal, or the tools it uses.' },
            {
              title: 'Click into an agent',
              body: 'Opening an agent shows its full workspace: **Overview** (goal, expected outcome, and its step-by-step execution plan), **Configuration**, **Tools & Capabilities**, and **Run History**.',
            },
            { title: 'Run it', body: 'Click **Run Agent** in the top right to run it against your connected org.' },
            { title: 'Review the results', body: 'Check the outputs it produces (reports, checklists, diffs, and similar) before treating its output as final.' },
          ],
          calloutsAfter: [
            { type: 'tip', text: 'Not sure where to start? Apex Quality Fix Assistant and Apex Refactor Assistant are a good first stop for most legacy orgs.' },
          ],
        },
        {
          title: 'Creating a Custom Agent',
          timeRequired: '15 minutes',
          difficulty: 'Intermediate',
          prerequisites: 'Connected Salesforce org (Section 2)',
          steps: [
            { title: 'Click Create Agent', body: 'Top right of the Agents page.' },
            { title: 'Define its goal', body: 'Describe in Natural Language what you want the agent to do.' },
            { title: 'Choose its tools', body: 'Select which tools and capabilities the agent is allowed to use to get there.' },
            { title: 'Save and run it', body: 'Save the agent, run it against your org, and refine its goal or tools based on the results.' },
          ],
          calloutsAfter: [
            { type: 'tip', text: "You can create as many custom agents as you need — there's no cap on how many you build alongside the prebuilt library." },
          ],
        },
      ],
    },
    {
      number: 5,
      title: "Let's Weave",
      overviewLede:
        'Weave is the center of nCoder.ai — a chat interface where you can ask a question, refactor a class, create new objects, and more, all in Natural Language. Quick questions get one agent; complex audits and changes get a small team of agents that fans out, cites artifacts, and proposes fixes.',
      overviewImage: {
        src: '/release-notes/weave.jpg',
        alt: "nCoder.ai Let's Weave chat interface with a file panel and Agent Monitor",
        aspect: 1.8667,
      },
      procedures: [
        {
          title: 'Chatting with Weave',
          timeRequired: '2 minutes',
          difficulty: 'Beginner',
          prerequisites: 'Connected Salesforce org (Section 2)',
          steps: [
            { title: 'Open Weave', body: 'Click **Weave** under the Agents section in the left-hand sidebar.' },
            {
              title: 'Give it context (optional)',
              body: 'Browse the file panel on the left — grouped by metadata type — to point Weave at a specific class, object, or component, or just ask in the open.',
            },
            {
              title: 'Ask your question',
              body: 'Type what you want in Natural Language. For example: "What are the most complex classes in this org?", "Which automation runs on every Lead creation?", "Audit risk in this org\'s data model," or "Find untested Apex with high call volume."',
            },
            { title: 'Watch it work', body: 'Quick questions get a single agent. Complex audits or changes get a small team that fans out, cites artifacts, and proposes fixes.' },
            { title: 'Check the Agent Monitor', body: 'The right-hand panel tracks Workspace, Impact Analysis, Test Classes, and Dependencies live as the conversation progresses.' },
          ],
          calloutsAfter: [
            {
              type: 'tip',
              text: "Not sure where to start with nCoder.ai at all? Start here — Weave can point you to the right agent, template, or tool for almost anything.",
            },
          ],
          subsections: [
            {
              title: 'Democratizing knowledge',
              lede: 'A simple question — "What are the most complex classes in this org?" — produces a wealth of information that used to live in the heads of a few. Now it\'s available to everyone. That\'s the whole point: democratize knowledge.',
              calloutsAfter: [
                {
                  type: 'note',
                  text: 'Behind the scenes, the request moves through an Intake → Planner → Tools → Workers → Synthesis pipeline, visible in the top bar as each stage completes. The Agent Monitor on the right tracks token usage, tool calls, and the artifacts the run produces.',
                },
              ],
            },
          ],
        },
        {
          title: 'Attaching Files, Voice & Docs',
          timeRequired: '2 minutes',
          difficulty: 'Beginner',
          prerequisites: 'None',
          steps: [
            { title: 'Attach a file', body: 'Click the paperclip icon to attach an image, PDF, code file, or document for Weave to reference while it works.' },
            { title: 'Use voice', body: 'Click the microphone icon to dictate your prompt instead of typing it.' },
            { title: 'Browse Salesforce docs', body: 'Click **Docs** to search the latest official Salesforce documentation without leaving the chat.' },
            { title: 'Send', body: 'Click **Send** (or press Enter) to kick things off.' },
          ],
          calloutsAfter: [
            {
              type: 'note',
              text: "The status pill in the top bar (Idle / working) and the SF connected indicator tell you at a glance whether Weave is ready and which org it's talking to.",
            },
          ],
        },
      ],
    },
    {
      number: 6,
      title: 'Migrations',
      overviewLede:
        'Migrations is a library of reusable automation templates that run against your connected org — everything from read-only analysis reports to templates that actively rewrite legacy components into modern equivalents.',
      overviewImage: {
        src: '/release-notes/migrations.jpg',
        alt: 'nCoder.ai Migrations library showing reusable automation templates tagged Read-only or Makes changes',
        aspect: 1.8568,
      },
      procedures: [
        {
          title: 'Browsing the Migrations Library',
          timeRequired: '5 minutes',
          difficulty: 'Beginner',
          prerequisites: 'Connected Salesforce org (Section 2)',
          steps: [
            { title: 'Open Migrations', body: 'Click **Migrations** in the left-hand sidebar.' },
            {
              title: 'Browse or search',
              body: 'Templates cover things like Apex Test Coverage Report, Technical Debt Analysis, Aura Component to LWC, Process Builder to Flow, Profile to Permission Set, Visualforce Page to LWC, Workflow Rule to Flow, Apex Callout to Named Credential, Permission Model Audit, and Unused Permission Sets.',
            },
            {
              title: 'Check the category and badge',
              body: "Each template is tagged by category (Analysis, Migration, Refactor, or Security) and by whether it's **Read-only** or **Makes changes** to your org.",
            },
          ],
          calloutsAfter: [
            {
              type: 'tip',
              text: 'Read-only templates — like Apex Test Coverage Report, Technical Debt Analysis, Permission Model Audit, and Unused Permission Sets — are always safe to run first and are a good way to get your bearings.',
            },
          ],
        },
        {
          title: 'Running a Migration Template',
          timeRequired: '30 minutes – 2 hours',
          difficulty: 'Intermediate',
          prerequisites: "Reviewed the template's description and tool list",
          calloutsBefore: [
            {
              type: 'caution',
              text: 'For templates marked **Makes changes** (like Aura Component to LWC or Workflow Rule to Flow), review every diff and decommission plan before treating it as final — same as you would for a pull request.',
            },
          ],
          steps: [
            { title: 'Open a template', body: 'Pick one from the library, for example Workflow Rule to Flow or Profile to Permission Set.' },
            { title: 'Review what it does', body: "Each template's description explains exactly what it scans or changes, plus how many tools it uses." },
            { title: 'Run it', body: 'Run the template against your connected org.' },
            { title: 'Review the output', body: 'Check the diffs, decommission plan, or report it produces before applying or merging anything downstream.' },
          ],
          calloutsAfter: [
            { type: 'note', text: "Nothing a template changes is final until you review and apply it — your original components stay intact until you say so." },
          ],
        },
      ],
    },
    {
      number: 7,
      title: 'Scheduling',
      overviewLede:
        'Scheduling lets you automate recurring maintenance jobs — RAG re-indexing, security scans, and metadata refreshes — at your own comfort, so your org stays current without anyone having to remember to run them by hand.',
      procedures: [
        {
          title: 'Creating a Schedule',
          timeRequired: '5 minutes',
          difficulty: 'Beginner',
          prerequisites: 'Connected Salesforce org (Section 2)',
          steps: [
            { title: 'Open Scheduling', body: 'Click **Scheduling** under the Agents section in the left-hand sidebar.' },
            { title: 'Start a new schedule', body: 'Click to create a **New Schedule**, then pick a job type and define the org scope.' },
            { title: 'Choose a run type', body: 'Select **RAG Re-index**, **Security Scan**, or **Metadata Refresh**.' },
            { title: 'Set the frequency and time', body: 'Choose how often it should run (for example, Daily) and at what time (for example, 09:00 AM).' },
            { title: 'Save it', body: 'Click **Save Schedule**. You can cancel at any point before saving if you change your mind.' },
          ],
          image: {
            src: '/release-notes/scheduling.jpg',
            alt: 'nCoder.ai New Schedule form with run type, frequency, and time options',
            aspect: 1.8543,
          },
          calloutsAfter: [
            {
              type: 'tip',
              text: 'Each run type explains itself right on the form — for example, RAG Re-index notes "Re-indexes all org metadata into the RAG knowledge base. No additional inputs needed."',
            },
          ],
        },
      ],
    },
    {
      number: 8,
      title: 'Reports',
      overviewLede:
        'Reports is your Run History — every completed migration and agent execution across your org, in one searchable, filterable list. Review outcomes, open saved results, and download the artifacts each run produced.',
      procedures: [
        {
          title: 'Viewing a Report',
          timeRequired: '5 minutes',
          difficulty: 'Beginner',
          prerequisites: 'At least one migration or agent run',
          steps: [
            { title: 'Open Reports', body: 'Click **Reports** under the Delivery section in the left-hand sidebar.' },
            { title: 'Filter by type', body: 'Switch between **All**, **Migrations**, and **Agents** to narrow the list to the kind of run you\'re after.' },
            {
              title: 'Filter by status',
              body: 'Use the status pills — **Running**, **Done**, **Cached**, **Stopped**, **Error** — or search by name or slug to find a specific run.',
            },
            { title: 'Open a run', body: "Each row shows the run's name, type, status, and duration. Click **open** to see the full detail and any artifacts it produced." },
            { title: 'Stop a run (if needed)', body: 'Runs still in progress show a **stop** action next to them.' },
          ],
          image: {
            src: '/release-notes/reports.jpg',
            alt: 'nCoder.ai Reports page showing Run History filtered by type and status',
            aspect: 1.8592,
          },
          calloutsAfter: [
            { type: 'tip', text: "If a migration template errors out, check its Reports entry first — the error status links straight to what went wrong." },
          ],
        },
      ],
    },
    {
      number: 9,
      title: 'MCP',
      overviewLede:
        'MCP is where live MCP tools, connector setup, and external service credentials live in one place. We currently support connectivity to Jira and Notion, with more connectors coming in future releases.',
      procedures: [
        {
          title: 'Connecting an MCP Server',
          timeRequired: '5 minutes',
          difficulty: 'Beginner',
          prerequisites: "An account with the provider you're connecting (e.g. Jira, Notion)",
          calloutsBefore: [{ type: 'note', text: "Chat (Weave) can only use a provider's tools after it's connected here." }],
          steps: [
            { title: 'Open MCP', body: 'Click **MCP** under the Delivery section in the left-hand sidebar.' },
            { title: 'Pick a provider', body: 'Under **Connect a new MCP**, choose **Jira** or **Notion**.' },
            { title: 'Name the connection', body: 'Give it a connection name (defaults to the provider name).' },
            {
              title: 'Continue and authorize',
              body: "Click **Continue with <Provider>** — this opens the provider's login and consent screen. Tokens are stored server-side by the MCP gateway, not in nCoder.ai directly.",
            },
            { title: 'Browse available tools', body: 'Once connected, use **Refresh tools** or search to see what\'s now available under Available MCP tools.' },
          ],
          image: {
            src: '/release-notes/mcp.jpg',
            alt: 'nCoder.ai MCP page showing connector setup for Jira and Notion and available MCP tools',
            aspect: 1.7789,
          },
          calloutsAfter: [
            {
              type: 'tip',
              text: "The Live MCP Tools, Tool Families, and Connectors counters at the top of the page update as soon as a provider is connected — if they're still at 0, double-check the connection went through.",
            },
          ],
        },
      ],
    },
    {
      number: 10,
      title: 'Git & Pipelines',
      overviewLede:
        "Git & Pipelines connects nCoder.ai to your source control and CI/CD, so agent and migration output can flow straight into your existing developer workflow. Everything you'd normally do from Git itself — create a repo, branch, check in changes, check out a branch, raise a pull request — you can do right here without leaving nCoder.",
      overviewImage: {
        src: '/release-notes/git-and-pipelines.jpg',
        alt: 'nCoder.ai Git & Pipelines screen for connecting source control and running Git operations',
        aspect: 1.8519,
      },
      procedures: [
        {
          title: 'Connecting a Git Repository',
          timeRequired: '10 minutes',
          difficulty: 'Intermediate',
          prerequisites: 'GitHub account with repo admin access',
          steps: [
            { title: 'Open Git & Pipelines', body: 'Click **Git & Pipelines** under the Delivery section in the left-hand sidebar.' },
            {
              title: 'Connect your repository',
              body: "nCoder reuses the same GitHub credentials already authorized for your account — there's no separate token or OAuth grant to set up. Select the repository from your connected GitHub account.",
            },
            { title: 'Select the target branch', body: 'Choose which branch agents and migrations should open pull requests against.' },
            { title: 'Save', body: 'Save the connection so agents and migrations can open pull requests directly.' },
          ],
          calloutsAfter: [
            { type: 'note', text: "Because the connection reuses your existing GitHub authorization, there's no separate credential setup step for Git & Pipelines." },
          ],
        },
        {
          title: 'Working with Git from nCoder',
          timeRequired: '5 minutes',
          difficulty: 'Beginner',
          prerequisites: 'Connected Git repository (Section 10)',
          lede: "Once a repository is connected, Git & Pipelines gives you the same day-to-day Git operations you'd otherwise run from the command line or your Git provider's UI — all without leaving nCoder.",
          steps: [
            { title: 'Create a repository', body: "Spin up a new repository directly from Git & Pipelines if you don't already have one connected." },
            { title: 'Create a branch', body: 'Branch off your target branch to isolate work coming from an agent, a migration, or a manual change.' },
            { title: 'Check in changes', body: "Commit changes — your own or an agent's — to the current branch, with a commit message." },
            { title: 'Check out a branch', body: 'Switch the active branch to review or continue work on it.' },
            { title: 'Raise a pull request', body: 'Open a PR from your branch back to the target branch for review, with the diff visible right in nCoder.' },
            { title: 'Merge or close', body: "Once the PR is approved, merge it — or close it out — without switching tools." },
          ],
          calloutsAfter: [
            {
              type: 'tip',
              text: 'Agents and migrations use these same operations automatically when they open pull requests on your behalf — this page just lets you do it by hand too.',
            },
          ],
        },
      ],
    },
    {
      number: 11,
      title: 'Org 360',
      overviewLede:
        "Org 360 is a visual, interactive graph of your entire org's metadata — the Graph RAG that powers everything else in nCoder.ai. It's the nucleus of the product: every object, class, flow, and component is a node, connected by its real relationships.",
      procedures: [
        {
          title: 'Exploring the Org 360 Graph',
          timeRequired: '10 minutes',
          difficulty: 'Beginner',
          prerequisites: 'Connected Salesforce org (Section 2)',
          steps: [
            { title: 'Open Org 360', body: 'Click **Org 360** under the Delivery section in the left-hand sidebar.' },
            {
              title: 'Filter by metadata type',
              body: 'Use the Metadata Types panel to filter the graph down to a specific type — CustomObject, ApexClass, Flow, Layout, and so on — each with a live count.',
            },
            { title: 'Click a node', body: 'Selecting any node opens the **Inspector** panel with its name, type, and path.' },
            {
              title: 'Review Details',
              body: 'The Details tab breaks the node down into **References** (what it depends on), **Referenced By** (what depends on it), and **Fields**.',
            },
            { title: 'Check Blast Radius', body: 'Switch to the **Blast Radius** tab to see how far a change to this component would ripple before you touch anything.' },
          ],
          image: {
            src: '/release-notes/org-360.jpg',
            alt: "nCoder.ai Org 360 graph with a node's Inspector panel open, showing References and Referenced By",
            aspect: 1.847,
          },
          calloutsAfter: [
            {
              type: 'tip',
              text: "Org 360 is a good first stop before running any Migration template or Agent — understanding a component's References, Referenced By, and Blast Radius up front makes the output of everything downstream easier to trust.",
            },
          ],
        },
      ],
    },
    {
      number: 12,
      title: 'Salesforce Releases',
      overviewLede:
        "Salesforce Releases keeps you ahead of upcoming platform release changes. Upload a Salesforce release notes PDF and nCoder.ai cross-references it against your org's actual metadata and code, so you know exactly what's breaking, deprecated, or worth reviewing before the release goes live.",
      procedures: [
        {
          title: 'Checking Release Readiness',
          timeRequired: '10 minutes',
          difficulty: 'Beginner',
          prerequisites: 'Connected Salesforce org (Section 2), a Salesforce release notes PDF',
          steps: [
            { title: 'Open Salesforce Releases', body: 'Click **Salesforce releases** under the Delivery section in the left-hand sidebar.' },
            {
              title: 'Upload the release notes',
              body: "Click **Upload PDF** and select the Salesforce release notes for the upcoming release (for example, Summer '26). Give it a release name so you can find it again later.",
            },
            {
              title: 'Wait for it to process',
              body: "nCoder.ai cross-references the notes against your org's metadata and code — status shows as **Preparing...** while it works.",
            },
            {
              title: 'Review the buckets',
              body: 'Findings are sorted into **Breaking**, **Deprecated**, **Review**, **Improvement**, and **OK**, alongside a total count of **Components Affected**.',
            },
            {
              title: 'Filter and drill in',
              body: 'Use the bucket and metadata type filters, or click any cell in the **Severity heatmap** to jump to the specific components involved.',
            },
            { title: 'Export', body: 'Click **Export CSV** or **Export JSON** to share the findings with your team.' },
          ],
          image: {
            src: '/release-notes/salesforce-releases.jpg',
            alt: 'nCoder.ai Salesforce Releases page cross-referencing release notes against org metadata and code',
            aspect: 1.7766,
          },
          calloutsAfter: [
            { type: 'tip', text: "Anything in the Breaking bucket deserves a look before you plan your upgrade window — that's where actual functionality could stop working." },
          ],
        },
      ],
    },
    {
      number: 13,
      title: 'Deployment',
      overviewLede: 'Deployment is one centralized place to ship the code you refactored, authored, or Wove — straight to Salesforce, or pushed to a Git repository.',
      procedures: [
        {
          title: 'Deploying Your Changes',
          timeRequired: '10 minutes',
          difficulty: 'Beginner',
          prerequisites: 'Connected Salesforce org (Section 2), or a connected GitHub repository (Section 10)',
          steps: [
            { title: 'Open Deployment', body: 'Click **Deployment** under the Delivery section in the left-hand sidebar.' },
            { title: 'Choose a target', body: 'Select **Salesforce** to deploy to your org directly, or **GitHub** to push to a repository instead.' },
            { title: 'Stage your files', body: 'Any uncommitted changes appear on the left — stage the files you want included in this deploy.' },
            { title: 'Add a commit message (optional)', body: "Describe what you're deploying and why." },
            { title: 'Deploy', body: 'Click **Deploy**. Conflicts are detected before the deployment starts, and rollback on error is enabled by default.' },
          ],
          image: {
            src: '/release-notes/deployment.jpg',
            alt: 'nCoder.ai Deployment page with Salesforce and GitHub targets and a Deploy button',
            aspect: 1.7588,
          },
          calloutsAfter: [
            { type: 'tip', text: "If nothing shows under Files to deploy, you likely have no uncommitted changes yet — that's expected right after a fresh connection." },
          ],
        },
        {
          title: 'Reviewing Deploy History',
          timeRequired: '5 minutes',
          difficulty: 'Beginner',
          prerequisites: 'At least one prior deployment',
          steps: [
            { title: 'Open Deployment', body: 'Click **Deployment** under the Delivery section in the left-hand sidebar.' },
            { title: 'Switch to History', body: 'Click the **History** tab next to Deploy.' },
            { title: 'Review past deploys', body: 'See what was deployed, when, to which target, and its outcome.' },
          ],
          calloutsAfter: [
            {
              type: 'note',
              text: "Because rollback on error is enabled by default, a failed deployment shouldn't leave your org or repository in a half-applied state — but it's still worth confirming in History.",
            },
          ],
        },
      ],
    },
    {
      number: 14,
      title: 'Testing',
      overviewLede:
        'Testing is currently in **Beta** — describe a test in natural language and the agent generates a Playwright spec, you approve it, and it runs end-to-end against your sandbox in a real, live browser.',
      overviewCallouts: [
        {
          type: 'tip',
          label: 'Why this matters',
          text: 'This is the first time a product can impersonate a real user and run true end-to-end tests — clicking through an actual browser, exactly like a person would — all from one tool, without you writing a line of test code.',
        },
      ],
      procedures: [
        {
          title: 'Describing a Test in Natural Language',
          timeRequired: '5 minutes',
          difficulty: 'Beginner',
          prerequisites: 'Connected Salesforce org (Section 2), a sandbox to test against',
          steps: [
            { title: 'Open Testing', body: 'Click **Testing** under the Delivery section in the left-hand sidebar.' },
            { title: 'Start a new plan', body: "Click **New plan**, or switch to an existing session if you're continuing earlier work." },
            {
              title: 'Describe the test',
              body: 'Type what you want tested in Natural Language in the chat panel — for example, "Create an Account and verify it appears in the list," "Test that a required field blocks saving," or "Verify a lookup field resolves correctly." Example prompts are offered if you\'re not sure where to start.',
            },
            {
              title: 'Let it inspect your org',
              body: "The agent inspects your org's objects, fields, and record types to understand what it's testing against before writing anything.",
            },
          ],
          image: {
            src: '/release-notes/testing.jpg',
            alt: 'nCoder.ai Testing page (Beta) with a natural-language test prompt and live browser panel',
            aspect: 1.8494,
          },
          calloutsAfter: [
            {
              type: 'tip',
              text: "Be as specific as you'd be talking to a QA engineer — naming the object, field, or exact behavior you expect gets a more accurate test.",
            },
          ],
        },
        {
          title: 'Approving & Running the Test',
          timeRequired: '5-10 minutes',
          difficulty: 'Beginner',
          prerequisites: 'A drafted test plan',
          steps: [
            { title: 'Review the plan', body: "The agent proposes a plan based on your description — read through it, or edit it if it's not quite right." },
            { title: 'Approve it', body: 'Once the plan looks correct, approve it to generate the underlying Playwright spec.' },
            {
              title: 'Watch it run',
              body: 'The test executes against your sandbox in the **live browser** panel — you can watch it click, type, and navigate in real time, just like a person would.',
            },
            { title: 'Let it self-heal', body: 'If a step fails, the agent investigates and fixes the failure itself rather than just reporting a red X.' },
          ],
          calloutsAfter: [{ type: 'caution', text: 'This is a Beta feature — always run tests against a sandbox, never production.' }],
        },
      ],
    },
    {
      number: 15,
      title: 'Observability',
      overviewLede:
        'Observability lets you analyze the cost of any action you performed in nCoder.ai, in real time. Anyone on the team can see the cost and token usage (input and output) behind any action — full transparency, nothing hidden in a black box.',
      overviewCallouts: [
        {
          type: 'tip',
          label: 'Benchmark',
          text: 'In our internal testing against industry benchmarks, nCoder.ai uses 5-7x fewer tokens than any frontier LLM model to accomplish the same work.',
        },
      ],
      procedures: [
        {
          title: 'Reading the Observability Dashboard',
          timeRequired: '10 minutes',
          difficulty: 'Beginner',
          prerequisites: 'Connected Salesforce org (Section 2), at least one run performed',
          steps: [
            { title: 'Open Observability', body: 'Click **Observability** under the Delivery section in the left-hand sidebar.' },
            {
              title: 'Filter by product',
              body: 'Switch between **All Products**, **Agents**, **Migrations**, **Dev Agent**, and **AI with Weave** to scope the dashboard to a specific area.',
            },
            {
              title: 'Scan the top-line metrics',
              body: 'Agent Turns, Total Cost, Total Tokens (in/out), P95 Latency, Tool Calls, Service Runs, and Service Success give you an at-a-glance read on health and spend.',
            },
            {
              title: 'Check the trend and route charts',
              body: 'Agent Volume Trend shows daily turns over time; Route Distribution and Route Quality break volume, pass rate, latency, and cost down by individual route.',
            },
            { title: 'Drill in', body: 'Click any report element — a bar, a route row, a metric card — to inspect the underlying slice.' },
          ],
          image: {
            src: '/release-notes/observability.jpg',
            alt: 'nCoder.ai Observability dashboard with cost, token usage, and route quality metrics',
            aspect: 1.7544,
          },
          calloutsAfter: [
            {
              type: 'note',
              text: 'These are real, live numbers from your own usage — a low pass rate or high P95 latency here is worth investigating before you scale up a given agent or migration template.',
            },
          ],
        },
      ],
    },
    {
      number: 16,
      title: 'Salesforce Debugger',
      overviewLede: 'Salesforce Debugger inspects governor usage, SOQL/DML pressure, call flow, failing components, and fix evidence — directly inside nCoder.ai.',
      overviewCallouts: [
        {
          type: 'tip',
          label: 'Why this matters',
          text: "Ask any developer and they'll tell you how hard it is to debug anything in Salesforce. For the first time ever, a real debugger is readily available — no more piecing it together from raw debug logs by hand.",
        },
      ],
      procedures: [
        {
          title: 'Debugging an Issue',
          timeRequired: '10 minutes',
          difficulty: 'Intermediate',
          prerequisites: 'Connected Salesforce org (Section 2)',
          steps: [
            {
              title: 'Open Salesforce Debugger',
              body: 'Click **Salesforce Debugger** under the Delivery section in the left-hand sidebar. The workflow moves through four steps: **Select logs**, **Inspect raw log**, **Analyze**, and **Snapshot**.',
            },
            { title: 'Pick a trace user', body: 'Choose the user whose activity you want to capture, then click **Renew logs** to start capturing.' },
            { title: 'Reproduce the issue', body: 'Go reproduce the problem in Salesforce itself while the trace is active.' },
            {
              title: 'Reload and select logs',
              body: 'Back in nCoder.ai, click **Reload logs**, then select the log (or logs) that captured the issue — use **Select best logs** if you\'re not sure which ones matter.',
            },
            {
              title: 'Inspect the raw log',
              body: 'Click a log row to view its raw Salesforce log. Search within it for things like **USER_DEBUG**, **FATAL_ERROR**, **SOQL_EXECUTE**, or **CODE_UNIT_STARTED**.',
            },
            {
              title: 'Analyze',
              body: 'Click **Analyze** on your selected logs to get governor usage, SOQL/DML pressure, call flow, and the failing component with evidence for a fix.',
            },
          ],
          image: {
            src: '/release-notes/salesforce-debugger.jpg',
            alt: 'nCoder.ai Salesforce Debugger with a 4-step workflow: select logs, inspect, analyze, snapshot',
            aspect: 1.7522,
          },
          calloutsAfter: [
            {
              type: 'note',
              text: "If the trace user dropdown shows no users with active traces, that's expected until you pick a trace user and click Renew logs.",
            },
          ],
        },
      ],
    },
    {
      number: 17,
      title: 'Share Feedback',
      overviewLede: "Tell the nCoder team directly what's working, what's confusing, or what's broken — no ticketing system required.",
      procedures: [
        {
          title: 'Sharing Feedback',
          timeRequired: '1 minute',
          difficulty: 'Beginner',
          prerequisites: 'None',
          steps: [
            { title: 'Open Share Feedback', body: 'Click **Share feedback** in the left-hand sidebar, under Support.' },
            { title: 'Choose a feedback type', body: 'Pick **Rate experience** to score specific parts of the product, or **Report a bug** to flag something that isn\'t working.' },
            {
              title: 'Rate your experience',
              body: 'Give a star rating for the areas shown — Workflow, Agent configuration, Metadata explorer, and overall experience — plus how likely you are to recommend nCoder.',
            },
            { title: 'Submit', body: 'Click **Submit feedback** to send it straight to the nCoder team.' },
          ],
          calloutsAfter: [
            { type: 'tip', text: 'Rating individual areas (not just the overall score) helps the team see exactly where to focus next.' },
          ],
        },
      ],
    },
    {
      number: 18,
      title: 'Request a Feature',
      overviewLede: 'Have an idea for something nCoder should do? Send it directly to the team building the product.',
      procedures: [
        {
          title: 'Requesting a Feature',
          timeRequired: '1 minute',
          difficulty: 'Beginner',
          prerequisites: 'None',
          steps: [
            { title: 'Open Request a Feature', body: 'Click **Request a feature** in the left-hand sidebar, under Support.' },
            { title: 'Add a headline', body: 'Give your idea a short title so the team can identify it at a glance.' },
            { title: 'Describe the feature', body: "Explain what you'd like nCoder to do and how it would help you, in the Description field." },
            { title: 'Submit', body: 'Click **Submit request** to send it straight to the nCoder product team for review.' },
          ],
          calloutsAfter: [
            { type: 'note', text: 'Feature requests directly shape the nCoder roadmap — the team reviews every submission.' },
          ],
        },
      ],
    },
  ],
};
